import React, { useCallback, useEffect, useRef } from "react";
import { withRouter, useHistory, useLocation } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import Form from 'react-bootstrap/Form';

import FindingsService from "src/modules/3rd-party-management/apis/admin/FindingsService";
import { useMutation } from "@tanstack/react-query";

import { toast } from "react-toastify";
import DOMPurify from 'dompurify';

import Spinner from 'react-bootstrap/Spinner';
import { Badge, UncontrolledTooltip } from "reactstrap";
import RelevancyReasonModal from "./RelevancyReasonModal";
import FindingDates from "./FindingDates";

const FindingsListItem = (props) => {
    const location = useLocation();
    const history = useHistory();
    const { data, t, checked, onCheckStatusChanged } = props;
    const [isRead, setIsRead] = React.useState(!!data.is_read);
    const [relevancyData, setRelevancyData] = React.useState(data.relevancy ? {
        relevancy: data.relevancy?.relevancy,
        reasonText: data.relevancy?.reason_text
    } : null);
    const [showRelevancyReasonModal, setShowRelevancyReasonModal] = React.useState(false);
    const ref = useRef();

    const {
        mutate: mutateRelevancy,
        isLoading: isLoadingRelevancy
    } = useMutation({
        mutationFn: async (payload) => {
            const service = FindingsService.getInstance();

            return await service.updateRelevancy(data.id, payload);
        },
        onSuccess: (_respone, payload) => {
            setRelevancyData(payload);
            setShowRelevancyReasonModal(false);

            toast(t("Relevancy updated successfully."), {
                type: "success"
            });
        },
        onError: () => {
            toast(t("Failed to update relevancy."), {
                type: "error"
            });
        }
    });

    const handleToggleReadStatusMutation = useMutation({
        mutationFn: async (newsId) => {
            const service = FindingsService.getInstance();

            return await service.toggleRead(newsId);
        },
        onSuccess: () => {
            setIsRead(!isRead);
            toast(t("Read status updated successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while updating read status."), {
                type: "error",
            });
        }
    });

    const handleNotRelevancyReason = useCallback(({
        relevancy,
        reasonType,
        reasonText
    }) => {
        mutateRelevancy({
            relevancy: relevancy,
            reasonType: reasonType,
            reasonText: reasonText

        });
    }, [mutateRelevancy]);

    useEffect(() => {
        if (ref.current && ref.current.checked !== checked) {
            ref.current.checked = checked;
            onCheckStatusChanged && onCheckStatusChanged(checked, data);
        }
    }, [checked]);

    const goToDetailPage = () => {
        const searchParams = new URLSearchParams(location.search);
        
        if (!searchParams.has('findings')) {
            searchParams.set('tab', 'findings');
            
        }

        searchParams.set('id', data.id);
        
        history.push({
            search: searchParams.toString()
        });
    }

    const renderTags = () => {
        switch (data.tag) {
            case 'adverse_news':
                return (
                    <>
                        <br />
                        <Badge
                            color="danger"
                            pill
                            className="me-2 d-inline-flex align-items-center gap-1 bg-danger"
                        >
                            {t('Adverse News')}
                        </Badge>
                    </>
                );
            case 'country_adverse_news':
                return (
                    <>
                        <br />
                        <Badge
                            color="warning"
                            pill
                            className="me-2 d-inline-flex align-items-center gap-1 bg-warning"
                        >
                            {t('Country Adverse News')}
                        </Badge>
                    </>
                );
            case 'investigative_adverse_news':
                return (
                    <>
                        <br />
                        <Badge
                            color="warning"
                            pill
                            className="me-2 d-inline-flex align-items-center gap-1"
                            style={{
                                background: '#F59E0B'
                            }}
                        >
                            {t('Investigative Adverse News')}
                        </Badge>    
                    </>
                );
            case 'general_news':
                return (
                    <>
                        <br />
                        <Badge
                            color="info"
                            pill
                            className="me-2 d-inline-flex align-items-center gap-1 bg-info"
                        >
                            {t('General')}
                        </Badge>
                    </>
                );
            case 'social_media':
                return (
                    <>
                        <br />
                        <Badge
                            color="info"
                            pill
                            className="me-2 d-inline-flex align-items-center gap-1 bg-primary"
                        >
                            {t('Social Media')}
                        </Badge>
                    </>
                );
            case 'ai_adverse_news':
                return (
                    <>
                        <br />
                        <Badge
                            color="warning"
                            pill
                            className="me-2 d-inline-flex align-items-center gap-1 bg-warning"
                        >
                            {t('AI Adverse News')}
                        </Badge>
                    </>
                );
            default:
                return null;
        }
    }

    const displayRelevancyScore = data && ('relevancyScore' in data);

    const relevancyScore = parseInt(data?.relevancyScore);

    const itemImage = data.is_bafa ? `/images/bafa/${data.image}.jpg` : `/images/findings/${data.image}.jpg`;

    return (
        <>
            <div className={`finding-list-item mb-2 ${isRead ? 'read' : ''}`}>
                {/* <div className="finding-list-item-checkbox">
                    <Form.Check type="checkbox" ref={ (itemRef) => ref.current = itemRef} onChange={(e) => {onCheckStatusChanged && onCheckStatusChanged(e.target.checked, data)}} />
                </div> */}

                <div className="finging-container d-flex align-items-center">
                    <div className="image-container me-4">
                        <img src={itemImage} alt="" />
                    </div>
                    <div className="finding-content d-flex flex-column align-items-start w-100">
                        <div className="finding-content-row d-flex align-items-center justify-content-between w-100 mb-1">
                            <span className="category-title">
                                {data.news_agency}
                            </span>

                            <div className="d-flex align-items-center">
                                <div className="relevance-buttons d-flex gap-2 me-3">
                                    <button
                                        className="flag-btn"
                                        id={`relevant-true-${data.id}`}
                                        disabled={(isLoadingRelevancy && !showRelevancyReasonModal) || (relevancyData && relevancyData.relevancy)}
                                        onClick={() => handleNotRelevancyReason({
                                            relevancy: true,
                                            reasonType: '',
                                            reasonText: ''
                                        })}
                                    >
                                        <UncontrolledTooltip
                                            placement="top"
                                            target={`relevant-true-${data.id}`}
                                            trigger="hover"
                                        >
                                            {(relevancyData && relevancyData.relevancy) ? t("Already marked as relevant") : t("Mark as relevant")}
                                        </UncontrolledTooltip>
                                        {(isLoadingRelevancy && !showRelevancyReasonModal) ? (
                                            <Spinner size="sm" className="me-2" />
                                        ) : (
                                            <i
                                                className={`ri-thumb-up-fill font-size-20 ${(relevancyData && !relevancyData.relevancy) ? 'text-muted' : ''}`}
                                            ></i>
                                        )}
                                    </button>

                                    <button
                                        className="flag-btn"
                                        id={`relevant-false-${data.id}`}
                                        onClick={() => setShowRelevancyReasonModal(true)}
                                        disabled={relevancyData && !relevancyData.relevancy}
                                    >
                                        <UncontrolledTooltip
                                            placement="top"
                                            target={`relevant-false-${data.id}`}
                                            trigger="hover"
                                        >
                                            {(relevancyData && !relevancyData.relevancy) ? `${t("Already marked as not relevant")} (${t("Reason")}: ${t(relevancyData?.reasonText)})` : `${t("Mark as not relevant")}`}
                                        </UncontrolledTooltip>
                                        <i
                                            className={`ri-thumb-down-fill font-size-20 ${relevancyData && relevancyData.relevancy ? 'text-muted' : ''}`}
                                        ></i>
                                    </button>
                                </div>

                                <button
                                    className="flag-btn"
                                    id={`toggle-read-${data.id}`}
                                    disabled={handleToggleReadStatusMutation.isLoading}
                                    onClick={() => {
                                        handleToggleReadStatusMutation.mutate(data.id)
                                    }}
                                >
                                    <UncontrolledTooltip
                                        placement="top"
                                        target={`toggle-read-${data.id}`}
                                        trigger="hover"
                                    >
                                        {isRead ? props.t("Mark as unread") : props.t("Mark as read")}
                                    </UncontrolledTooltip>
                                    {
                                        handleToggleReadStatusMutation.isLoading ? (
                                            <Spinner animation="border" variant="primary" size="sm" />
                                        ) : (
                                            isRead ? (
                                                <i className="ri-checkbox-circle-fill font-size-20 text-success"></i>
                                            ) : (
                                                <i className="ri-checkbox-blank-circle-line font-size-20"></i>
                                            )
                                        )
                                    }
                                </button>
                            </div>
                        </div>

                        <div className="finding-content-row d-flex align-items-center justify-content-start flex-wrap w-100 mb-1">
                            {data.weight ? (
                                <i
                                    style={{
                                        color: `#${data.weight}`
                                    }}
                                    className="ri-checkbox-blank-circle-fill me-2"
                                ></i>
                            ) : null}

                            <h4 onClick={goToDetailPage} className="finding-title fw-bolder m-0">
                                {data.title}
                                {renderTags()}
                            </h4>

                            {displayRelevancyScore && (
                                <div className="relevancy-score w-100 mb-1 mt-1">
                                    {t('Relevancy Score')}:
                                    <span className={'score-value ml-1 ' + (relevancyScore < 40 ? 'danger' : (relevancyScore <= 69 ? 'warning' : 'success'))}>
                                        {relevancyScore}%
                                    </span>
                                </div>
                            )}

                            <a href={data.source_link} rel="noreferrer" target="_blank" className="d-block w-100 link">
                                {data.source_link}
                            </a>
                        </div>

                        <div className="finding-content-row mb-1">
                            <div className="pt-1 w-100 m-0 desc" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.lead_text) }} />
                        </div>

                        {!data?.is_bafa && (data?.release_date || data?.created_at) && (
                            <div className="finding-content-row mb-1">
                                <FindingDates finding={data} t={t} />
                            </div>
                        )}

                        <div className="finding-content-row">
                            <div className="key-words">
                                <span className="badge me-1">{t(data?.keyword)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <RelevancyReasonModal
                isOpen={showRelevancyReasonModal}
                toggle={() => setShowRelevancyReasonModal(!showRelevancyReasonModal)}
                onSave={handleNotRelevancyReason}
                isLoading={isLoadingRelevancy}
            />
        </>
    );
};

export default withNamespaces()(
    withRouter(FindingsListItem)
);
